import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef, FormEvent } from 'react';

/**
 * Landing page in the msgmanager.com pattern, with TriNetHub's Cisco/Webex
 * positioning: WxC, WxCC, CUCM, UCCX, CUBE/LGW integration + Cisco AI Agent
 * video embed. Single sign-in flow (super-admin and tenant-admin both /login)
 * with role hint via URL.
 */
export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Nav />
      <Hero />
      <Capabilities />
      <ModulesGrid />
      <AiAgent />
      <Migration />
      <HowItWorks />
      <Security />
      <Faq />
      <FinalCta />
      <Footer />
      <LandingStyles />
    </div>
  );
}

// ─── Logo ────────────────────────────────────────────────────────────────────
function LogoMark({ size = 32 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <defs>
        <linearGradient id="tnh-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0a84ff" />
          <stop offset="100%" stopColor="#00d4ff" />
        </linearGradient>
      </defs>
      <rect x="10" y="8" width="44" height="9" rx="2" fill="url(#tnh-grad)" />
      <rect x="27" y="8" width="10" height="34" rx="2" fill="url(#tnh-grad)" />
      <path d="M8 50 Q20 42 32 50 T56 50" stroke="url(#tnh-grad)" strokeWidth="2.4" fill="none" strokeLinecap="round" />
      <path d="M10 56 Q22 50 32 56 T54 56" stroke="url(#tnh-grad)" strokeWidth="1.8" fill="none" strokeLinecap="round" opacity="0.7" />
      <path d="M14 60 Q24 56 32 60 T50 60" stroke="url(#tnh-grad)" strokeWidth="1.4" fill="none" strokeLinecap="round" opacity="0.45" />
    </svg>
  );
}

// ─── Sticky nav ──────────────────────────────────────────────────────────────
function Nav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);
  return (
    <header className={`sticky top-0 z-40 transition-all ${scrolled ? 'bg-white/85 backdrop-blur border-b border-slate-200' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2.5">
          <LogoMark size={28} />
          <span className="font-semibold tracking-tight text-lg text-slate-900">TriNetHub</span>
        </a>
        <nav className="hidden md:flex items-center gap-6 text-sm text-slate-600">
          <a href="#modules"   className="hover:text-slate-900">Modules</a>
          <a href="#aiagent"   className="hover:text-slate-900">AI Agent</a>
          <a href="#migration" className="hover:text-slate-900">Migration</a>
          <a href="#security"  className="hover:text-slate-900">Security</a>
          <a href="#faq"       className="hover:text-slate-900">FAQ</a>
        </nav>
        <div className="flex items-center gap-2">
          <DashboardMenu />
          <Link to="/login?role=super_admin"  className="hidden sm:inline-flex px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-medium">Super-Admin</Link>
          <Link to="/login?role=tenant_admin" className="px-4 py-2 rounded-lg bg-[#0a84ff] hover:bg-[#0671e0] text-white text-sm font-semibold shadow-sm">Tenant Login</Link>
        </div>
      </div>
    </header>
  );
}

// ─── Dashboard subdomain dropdown ───
// "Go to your dashboard" — operator types their tenant subdomain and is sent to
// <tenant>.trinethub.com (the multi-tenant front door: clb, demo, any tenant).
function DashboardMenu() {
  const [open, setOpen] = useState(false);
  const [sub, setSub] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  function go(e: FormEvent) {
    e.preventDefault();
    const clean = sub
      .trim().toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/\.trinethub\.com.*$/, '')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/^-+|-+$/g, '');
    if (!clean) return;
    window.location.href = `https://${clean}.trinethub.com`;
  }

  const btnClass = 'border-slate-300 text-slate-700 hover:bg-slate-50';

  return (
    <div ref={wrapRef} className="relative hidden sm:block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true" aria-expanded={open}
        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${btnClass}`}
      >
        Dashboard
        <span className={`text-xs transition-transform ${open ? 'rotate-180' : ''}`}>⌄</span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-72 rounded-xl border border-slate-200 bg-white shadow-2xl p-4 text-slate-900 z-50">
          <div className="font-semibold text-sm">Go to your dashboard</div>
          <p className="text-xs text-slate-500 mt-1 mb-3">
            Enter your tenant subdomain to be redirected to your login page.
          </p>
          <form onSubmit={go}>
            <div className="flex items-stretch rounded-lg border border-slate-300 overflow-hidden focus-within:border-[#0a84ff] focus-within:ring-2 focus-within:ring-[#0a84ff]/20">
              <input
                autoFocus value={sub} onChange={(e) => setSub(e.target.value)}
                placeholder="yourtenant" aria-label="Tenant subdomain"
                className="flex-1 min-w-0 px-3 py-2 text-sm outline-none"
              />
              <span className="px-2 grid place-items-center text-xs text-slate-400 bg-slate-50 border-l border-slate-200 whitespace-nowrap">
                .trinethub.com
              </span>
            </div>
            <button type="submit" className="mt-2 w-full px-3 py-2 rounded-lg bg-[#0a84ff] hover:bg-[#0671e0] text-white text-sm font-semibold">
              Go to dashboard →
            </button>
          </form>
          <div className="mt-3 text-[11px] text-slate-400">
            Example: <span className="font-mono text-slate-500">demo.trinethub.com</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Hero ────────────────────────────────────────────────────────────────────
function Hero() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  function handleSend(e: FormEvent) {
    e.preventDefault();
    nav(`/login?email=${encodeURIComponent(email.trim())}`);
  }
  return (
    <section id="top" className="relative overflow-hidden bg-gradient-to-br from-[#0b1838] via-[#0c1f44] to-[#0a1730] text-white">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -left-24 w-[36rem] h-[36rem] rounded-full bg-[#0a84ff] opacity-[0.12] blur-3xl tnh-pulse" />
        <div className="absolute -bottom-40 -right-16 w-[28rem] h-[28rem] rounded-full bg-[#00d4ff] opacity-[0.10] blur-3xl tnh-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 md:pt-24 md:pb-28 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-400/10 border border-emerald-400/30 text-emerald-300 text-xs font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Edge-deployed on Cloudflare · 99.99% uptime
          </div>
          <h1 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
            The unified control plane for<br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3aa6ff] to-[#00d4ff]">
              Cisco &amp; Webex voice operations.
            </span>
          </h1>
          <p className="mt-5 text-slate-300 text-base md:text-lg max-w-xl">
            One console for WxC, WxCC, CUCM, UCCX, and CUBE/LGW. Provision, monitor, and migrate
            your entire Cisco/Webex estate without switching tools.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#modules" className="px-6 py-3 rounded-lg bg-[#0a84ff] hover:bg-[#0671e0] text-white font-semibold text-sm shadow-lg shadow-[#0a84ff]/30">
              See modules →
            </a>
            <a href="#aiagent" className="px-6 py-3 rounded-lg border border-slate-500 hover:border-slate-300 text-white font-semibold text-sm">
              Watch Cisco AI Agent demo
            </a>
          </div>

          <dl className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
            <div>
              <dt className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-b from-[#3aa6ff] to-[#00d4ff]">5</dt>
              <dd className="text-xs text-slate-400 mt-1">Cisco/Webex modules unified</dd>
            </div>
            <div>
              <dt className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-b from-[#3aa6ff] to-[#00d4ff]">&lt;60s</dt>
              <dd className="text-xs text-slate-400 mt-1">Hub → spoke command propagation</dd>
            </div>
            <div>
              <dt className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-b from-[#3aa6ff] to-[#00d4ff]">1</dt>
              <dd className="text-xs text-slate-400 mt-1">Console, every channel governed</dd>
            </div>
          </dl>
        </div>

        {/* Sign-in card */}
        <div className="lg:col-span-5">
          <div className="bg-white rounded-2xl shadow-2xl p-7 text-slate-900 max-w-md mx-auto lg:ml-auto">
            <div className="flex items-center gap-2.5 mb-1">
              <LogoMark size={28} />
              <span className="font-semibold tracking-tight text-lg">Operator sign-in</span>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Enter your authorized email — we'll send a 6-digit code.
            </p>
            <form onSubmit={handleSend} className="space-y-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Email</label>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  placeholder="you@yourtenant.gov"
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-[#0a84ff] focus:outline-none focus:ring-2 focus:ring-[#0a84ff]/20"
                />
              </div>
              <button type="submit" className="w-full px-4 py-2.5 rounded-lg bg-[#0a84ff] hover:bg-[#0671e0] text-white font-semibold text-sm">
                Send verification code →
              </button>
            </form>
            <div className="mt-4 flex items-center justify-between text-xs">
              <span className="text-slate-500">Protected by two-factor auth</span>
              <Link to="/login?role=super_admin" className="text-[#0a84ff] hover:underline font-medium">Super-Admin →</Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Core capabilities (4 cards) ─────────────────────────────────────────────
function Capabilities() {
  const items = [
    { title: 'One console, every system',  body: 'WxC, WxCC, CUCM, UCCX, CUBE/LGW — all administered from a single hub with per-tenant isolation.' },
    { title: 'AI-powered contact center',  body: 'Cisco AI Agent integration in WxCC: real-time transcripts, suggested replies, auto-summary on wrap-up.' },
    { title: 'Guided migrations',          body: 'CUCM → Webex Calling and UCCX → Webex Contact Center with discovery, dry-run, cutover, and rollback.' },
    { title: 'Edge-native, sub-second',    body: 'Cloudflare Workers globally distributed. Hub → spoke control plane propagates commands in seconds.' },
  ];
  return (
    <Section kicker="Core capabilities" headline="The platform your UC operators were missing.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((c) => (
          <div key={c.title} className="rounded-xl border border-slate-200 bg-white p-5 hover:border-[#0a84ff]/40 hover:shadow-lg transition">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#0a84ff] to-[#00d4ff] grid place-items-center text-white text-xs font-bold mb-3">✓</div>
            <div className="font-semibold">{c.title}</div>
            <p className="text-sm text-slate-600 mt-1">{c.body}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ─── Modules grid (5 cards) ──────────────────────────────────────────────────
function ModulesGrid() {
  const mods = [
    { tag: 'Webex', key: 'wxc',  title: 'Webex Calling',           sub: 'Numbers · queues · auto attendants · voicemail',     route: '/modules/wxc'  },
    { tag: 'Webex', key: 'wxcc', title: 'Webex Contact Center',    sub: 'Flows · queues · agents · AI agent assist',          route: '/modules/wxcc' },
    { tag: 'Cisco', key: 'cucm', title: 'CUCM',                    sub: 'Phones · partitions · route patterns · dial plan',   route: '/modules/cucm' },
    { tag: 'Cisco', key: 'uccx', title: 'UCCX',                    sub: 'Scripts · CSQs · agents · prompts',                  route: '/modules/uccx' },
    { tag: 'Cisco', key: 'cube', title: 'CUBE / LGW',              sub: 'Dial peers · SIP trunks · codec profiles',           route: '/modules/cube' },
  ];
  return (
    <Section id="modules" kicker="Modules" headline="Five Cisco/Webex systems. One operator console.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {mods.map((m) => (
          <Link key={m.key} to={m.route} className="rounded-xl border border-slate-200 bg-white p-5 hover:border-[#0a84ff]/40 hover:shadow-lg transition group">
            <div className="flex items-center justify-between mb-3">
              <span className={`pill text-[10px] tracking-wider uppercase font-semibold ${m.tag === 'Webex' ? 'bg-violet-100 text-violet-700' : 'bg-indigo-100 text-indigo-700'}`}>
                {m.tag}
              </span>
              <span className="text-slate-400 group-hover:text-[#0a84ff] transition">→</span>
            </div>
            <div className="font-semibold">{m.title}</div>
            <p className="text-sm text-slate-600 mt-1">{m.sub}</p>
          </Link>
        ))}
      </div>
    </Section>
  );
}

// ─── Cisco AI Agent video band ───────────────────────────────────────────────
function AiAgent() {
  return (
    <section id="aiagent" className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-[0.18em] text-[#00d4ff] uppercase font-semibold">Cisco AI Agent</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight leading-tight">
              Conversational AI for Webex Contact Center, governed from TriNetHub.
            </h2>
            <p className="mt-4 text-slate-300 text-base">
              Real-time transcripts, intent detection, agent assist, and post-call summaries — wired into your WxCC flows
              and audited through the same single console as the rest of your estate.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-slate-300">
              {[
                'Live transcript + sentiment per call',
                'Suggested replies and knowledge lookups during the call',
                'Auto-summary written into wrap-up at hang-up',
                'Routing decisions captured for QA + compliance review',
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#00d4ff] shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <div className="relative aspect-video w-full rounded-2xl overflow-hidden border border-slate-700 shadow-2xl bg-black">
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube.com/embed/p5Z3_IBTpH4?rel=0"
                title="AI Conversational intelligence for the Contact Center | Webex AI Agent"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
            <p className="text-xs text-slate-400 mt-3 text-center">
              Source: Cisco — <em>AI Conversational Intelligence for the Contact Center · Webex AI Agent</em>.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Migration showcase ──────────────────────────────────────────────────────
function Migration() {
  const [tab, setTab] = useState<'cucm' | 'uccx'>('cucm');
  const stages = ['Discover', 'Map', 'Validate', 'Cutover', 'Verify'];
  return (
    <Section id="migration" kicker="Migration" headline="From on-prem Cisco to cloud Webex — without the guesswork.">
      <div className="flex items-center gap-2 mb-6 border-b border-slate-200">
        <button onClick={() => setTab('cucm')}
                className={`px-4 py-2 -mb-px border-b-2 text-sm ${tab === 'cucm' ? 'border-[#0a84ff] text-[#0a84ff] font-medium' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
          CUCM → Webex Calling
        </button>
        <button onClick={() => setTab('uccx')}
                className={`px-4 py-2 -mb-px border-b-2 text-sm ${tab === 'uccx' ? 'border-[#0a84ff] text-[#0a84ff] font-medium' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
          UCCX → Webex Contact Center
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        {stages.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#0a84ff] to-[#00d4ff] grid place-items-center text-white text-xs font-bold">{i + 1}</div>
            <span className="text-sm font-medium">{s}</span>
            {i < stages.length - 1 && <span className="text-slate-300">→</span>}
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        {tab === 'cucm' ? (
          <div className="grid md:grid-cols-2 gap-4 text-sm text-slate-700">
            <div><strong>Discover.</strong> Pull CUCM device pools, partitions, CSS, translation patterns, route patterns, calling search spaces.</div>
            <div><strong>Map.</strong> Translate dial-plan and class-of-service to WxC locations, number ranges, and call queues.</div>
            <div><strong>Validate.</strong> Dry-run the proposed WxC config against a sandbox org and surface unsupported features.</div>
            <div><strong>Cutover.</strong> Phased per-site migration with WxC LGW survivability and parallel CUCM keep-alive.</div>
            <div className="md:col-span-2"><strong>Verify.</strong> Side-by-side call quality test, RTP loss + MOS scoring, rollback ready until you sign off.</div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4 text-sm text-slate-700">
            <div><strong>Discover.</strong> Inventory UCCX applications, scripts, triggers, CSQs, skills, and prompt libraries.</div>
            <div><strong>Map.</strong> Convert UCCX scripts into WxCC flow drafts; mirror CSQs to WxCC queues + skills.</div>
            <div><strong>Validate.</strong> Run the new flows against test calls and a sample CSV of intents from your historical data.</div>
            <div><strong>Cutover.</strong> Route pilot DIDs to WxCC, leave the rest on UCCX, ramp 10% → 50% → 100%.</div>
            <div className="md:col-span-2"><strong>Verify.</strong> Compare service-level metrics and agent state behaviour between WxCC and the old UCCX baseline.</div>
          </div>
        )}
      </div>
    </Section>
  );
}

// ─── How it works (4 steps) ──────────────────────────────────────────────────
function HowItWorks() {
  const steps = [
    { n: 1, t: 'Authenticate', d: 'Sign in with email + 6-digit OTP. JWT cookie issued for the role you own (super-admin or tenant-admin).' },
    { n: 2, t: 'Discover',    d: 'Spoke claims an identity, pulls tenant config, discovers your CUCM / UCCX / WxC / WxCC inventory.' },
    { n: 3, t: 'Operate',     d: 'Manage every system from the same console — numbers, queues, flows, agents, dial peers, SIP trunks.' },
    { n: 4, t: 'Migrate',     d: 'When ready, run the CUCM→WxC and UCCX→WxCC pipeline with full audit, dry-run, and rollback.' },
  ];
  return (
    <Section kicker="How it works" headline="Sign in to live operation in under 30 seconds.">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {steps.map((s) => (
          <div key={s.n} className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0a84ff] to-[#00d4ff] grid place-items-center text-white font-bold mb-3">{s.n}</div>
            <div className="font-semibold">{s.t}</div>
            <p className="text-sm text-slate-600 mt-1">{s.d}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ─── Security & governance ───────────────────────────────────────────────────
function Security() {
  const items = [
    { t: 'JWT + OTP two-factor',     d: 'Email-bound one-time codes; sessions signed HS256, 8-hour TTL, instant rotation.' },
    { t: 'HMAC-signed spoke channel', d: 'Every heartbeat, CDR, and command ACK is HMAC-SHA256 verified with replay defense.' },
    { t: 'AES-256-GCM credential vault', d: 'CUCM/UCCX/WxC/WxCC creds encrypted at rest; plaintext leaves the Worker once only.' },
    { t: 'Per-tenant isolation',     d: 'Tenant-admins scoped to their own tenant at the API layer. Super-admin is the only cross-tenant role.' },
    { t: 'Immutable audit log',      d: 'Every state-changing action recorded with who, what, when, before, after — hash-chained for tamper-evidence.' },
    { t: 'Edge-deployed on Cloudflare', d: 'Workers, D1, KV, Queues — globally distributed, DDoS-protected, zero single-region failure.' },
  ];
  return (
    <Section id="security" kicker="Security & governance" headline="Built like enterprise infrastructure.">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((i) => (
          <div key={i.t} className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="font-semibold">{i.t}</div>
            <p className="text-sm text-slate-600 mt-1">{i.d}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ─── FAQ ─────────────────────────────────────────────────────────────────────
function Faq() {
  const items = [
    { q: 'Does TriNetHub replace Webex Control Hub or CUCM admin?', a: 'No — it consolidates them. Your CUCM, UCCX, WxC, and WxCC keep doing what they do. TriNetHub gives operators a single governed entry point and a migration path between them.' },
    { q: 'How does it talk to my on-prem CUCM / UCCX / CUBE?',      a: 'A lightweight Spoke agent runs in your environment. It heartbeats outbound to the Hub over HTTPS (HMAC-signed) and applies commands the Hub queues for it. No inbound firewall change.' },
    { q: 'Is my Cisco/Webex configuration data isolated?',          a: 'Yes. Every tenant has its own row-level partition in D1 plus an AES-256-GCM credential vault. Tenant-admins only see their own tenant; super-admin is the only cross-tenant role.' },
    { q: 'What about Cisco AI Agent in WxCC?',                       a: 'TriNetHub administers and audits Cisco AI Agent through the WxCC module — flows, prompts, knowledge sources, and post-call summaries all governed from the same console.' },
    { q: 'How fast can I migrate CUCM to Webex Calling?',           a: 'A typical mid-sized cluster runs Discover → Validate in days, then phased cutover over 2–6 weeks. Every stage has rollback and side-by-side call testing.' },
  ];
  return (
    <Section id="faq" kicker="FAQ" headline="Questions teams ask before going live.">
      <div className="space-y-3">
        {items.map((i, idx) => (
          <details key={idx} className="rounded-xl border border-slate-200 bg-white p-5 group">
            <summary className="cursor-pointer flex items-center justify-between font-medium">
              {i.q}
              <span className="text-slate-400 group-open:rotate-180 transition">⌄</span>
            </summary>
            <p className="text-sm text-slate-600 mt-3">{i.a}</p>
          </details>
        ))}
      </div>
    </Section>
  );
}

// ─── Final CTA ───────────────────────────────────────────────────────────────
function FinalCta() {
  return (
    <section className="bg-gradient-to-br from-[#0b1838] to-[#0a1730] text-white">
      <div className="max-w-5xl mx-auto px-6 py-16 md:py-20 text-center">
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">Ready to unify your Webex estate?</h2>
        <p className="mt-4 text-slate-300 max-w-2xl mx-auto">
          A 30-minute walkthrough is all it takes to see your CUCM, UCCX, WxC, WxCC, and CUBE/LGW under one console.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a href="mailto:sales@trinethub.com?subject=TriNetHub%20demo%20request"
             className="px-6 py-3 rounded-lg bg-[#0a84ff] hover:bg-[#0671e0] text-white font-semibold text-sm shadow-lg shadow-[#0a84ff]/30">
            Request a walkthrough
          </a>
          <Link to="/login?role=tenant_admin" className="px-6 py-3 rounded-lg border border-slate-500 hover:border-slate-300 text-white font-semibold text-sm">
            Tenant Login
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ──────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="bg-white border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-2.5">
            <LogoMark size={24} />
            <span className="font-semibold tracking-tight">TriNetHub</span>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            The unified control plane for Cisco/Webex collaboration. Per-tenant administration, edge-deployed.
          </p>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Modules</div>
          <ul className="space-y-1.5 text-slate-600">
            <li><a href="#modules" className="hover:text-slate-900">Webex Calling</a></li>
            <li><a href="#modules" className="hover:text-slate-900">Webex Contact Center</a></li>
            <li><a href="#modules" className="hover:text-slate-900">CUCM</a></li>
            <li><a href="#modules" className="hover:text-slate-900">UCCX</a></li>
            <li><a href="#modules" className="hover:text-slate-900">CUBE / LGW</a></li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Platform</div>
          <ul className="space-y-1.5 text-slate-600">
            <li><a href="#migration" className="hover:text-slate-900">Migration</a></li>
            <li><a href="#aiagent"   className="hover:text-slate-900">Cisco AI Agent</a></li>
            <li><a href="#security"  className="hover:text-slate-900">Security</a></li>
            <li><Link to="/status"   className="hover:text-slate-900">Status</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Company</div>
          <ul className="space-y-1.5 text-slate-600">
            <li><a href="mailto:sales@trinethub.com" className="hover:text-slate-900">sales@trinethub.com</a></li>
            <li><Link to="/login?role=tenant_admin"  className="hover:text-slate-900">Tenant sign-in</Link></li>
            <li><Link to="/login?role=super_admin"  className="hover:text-slate-900">Super-admin sign-in</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 text-xs text-slate-400 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <span>© {new Date().getFullYear()} TriNetHub. All rights reserved.</span>
          <span>Independent third-party platform. Cisco, Webex, CUCM, UCCX, CUBE are trademarks of their respective owners.</span>
        </div>
      </div>
    </footer>
  );
}

// ─── Shared section shell + tiny styles ──────────────────────────────────────
function Section({ id, kicker, headline, children }: { id?: string; kicker: string; headline: string; children: React.ReactNode }) {
  return (
    <section id={id} className="bg-white">
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
        <div className="text-[10px] tracking-[0.18em] text-[#0a84ff] uppercase font-semibold">{kicker}</div>
        <h2 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight max-w-3xl">{headline}</h2>
        <div className="mt-8">{children}</div>
      </div>
    </section>
  );
}

function LandingStyles() {
  return (
    <style>{`
      @keyframes tnh-pulse { 0%,100% { transform: scale(1); opacity: 0.55; } 50% { transform: scale(1.06); opacity: 0.85; } }
      .tnh-pulse { animation: tnh-pulse 6s ease-in-out infinite; will-change: transform, opacity; }
      @media (prefers-reduced-motion: reduce) { .tnh-pulse { animation: none !important; } }
    `}</style>
  );
}
